import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import api from '../services/api';

/**
 * Shared state machine for "evidence viewer" modals (ImagePreviewModal,
 * NonNullEvidenceModal): which occurrence is selected, which evidence index
 * within it is showing, whether the present-only filter is active, and what
 * that sample's saved classification is.
 *
 * Extracted after four separate bugs -- all instances of the same root
 * cause -- were found and fixed ad hoc in ImagePreviewModal this session:
 * multiple independent useEffects each calling setCurrentIndex/
 * setSelectedOccurrenceId and reading each other's stale, pre-update
 * closures within the same render flush, so whichever effect's setState
 * call happened to be declared later silently won regardless of intent.
 * This hook implements the whole thing as ONE effect with explicit
 * pending-target refs, so that class of bug can't recur by construction.
 *
 * `currentIndex` is always the REAL index into `activeEvidence` (never a
 * "position within the filtered list") -- callers needing a "N of M"
 * display compute it via `filteredIndices.indexOf(currentIndex) + 1`.
 *
 * See docs/plans/2026-08-11-002-refactor-evidence-viewer-state-hook-plan.md
 * for the full design (state diagram + sequence diagram).
 */
export function useEvidenceViewerState({
  isOpen,
  anomalyId,
  evidenceList,
  sampleClassifications,
  deepLinkTarget, // {occurrenceId, evidenceIndex} | null
  // A deep link that names a specific evidenceIndex should be shown
  // regardless of presence -- the default present-only filter would
  // otherwise bounce away from a deep-linked item that happens to be
  // "absent" (the common case, since that's usually what a review was
  // about). Only defaults this way when the caller doesn't pass an explicit
  // value; a caller opening normally (no deep link) still defaults to true.
  initialShowPresentOnly = !(deepLinkTarget && Number.isInteger(deepLinkTarget.evidenceIndex)),
  onPersistClassifications,
  // When true, "Latest evidence" (no selectedOccurrenceId) only uses the
  // `sampleClassifications` prop if the latest fetched occurrence actually
  // has its own persisted classifications -- guards against applying a
  // stale anomaly-level cache (e.g. from a prior occurrence) against a
  // fresh occurrence's evidence, where index positions may not mean the
  // same thing. NonNullEvidenceModal's original behavior; ImagePreviewModal
  // never had this guard, so it defaults to false to leave that unchanged.
  requireLatestOccurrenceClassifications = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPresentOnly, setShowPresentOnly] = useState(initialShowPresentOnly);

  const [occurrences, setOccurrences] = useState([]);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState(null);
  const [occurrenceEvidence, setOccurrenceEvidence] = useState(null);
  const [occurrenceClassifications, setOccurrenceClassifications] = useState(null);
  const [occurrenceFetchInFlight, setOccurrenceFetchInFlight] = useState(false);

  const [localClassifications, setLocalClassifications] = useState([]);
  const localClassificationsRef = useRef(localClassifications);
  const initialClassificationsRef = useRef([]);
  useEffect(() => { localClassificationsRef.current = localClassifications; }, [localClassifications]);

  // Shared between the occurrence-list-loading effect and the seed/deep-link
  // effect below -- a ref (not state) so consuming it doesn't itself trigger
  // a render; the seed effect owns writing/clearing it.
  const deepLinkTargetRef = useRef(null);
  const openInitAppliedRef = useRef(false);
  // Reactive mirror of "a deep-linked occurrence is queued or being fetched"
  // -- deepLinkTargetRef itself doesn't trigger re-renders, but callers need
  // a render-visible flag covering the gap between the modal opening and the
  // occurrence fetch actually starting, not just its in-flight duration.
  const [deepLinkPending, setDeepLinkPending] = useState(false);

  // Mirrors selectedOccurrenceId for the occurrence-list-loading effect below
  // to consult synchronously -- deepLinkTargetRef alone only guards while a
  // deep link is still in flight; once it resolves and gets cleared, an
  // unrelated, later-resolving occurrence-list fetch would otherwise wipe out
  // the already-applied selection.
  const selectedOccurrenceIdRef = useRef(null);
  useEffect(() => { selectedOccurrenceIdRef.current = selectedOccurrenceId; }, [selectedOccurrenceId]);

  // Derived as a primitive boolean, not read directly off `occurrences`, in
  // the classification effect's deps below -- `occurrences` gets a fresh
  // array reference from every occurrence-list fetch even when its content
  // is unchanged, which would re-fire that effect on every such refetch and
  // silently revert any unsaved local classification edits back to the
  // canonical prop.
  const latestOccurrenceHasOwnClassifications = useMemo(
    () => occurrences.length > 0 && !!occurrences[0]?.sample_classifications,
    [occurrences]
  );

  // Memoized: an unstable `|| []` fallback reference here would make every
  // effect keyed on activeEvidence re-fire every render (the exact "Maximum
  // update depth exceeded" loop this hook exists to prevent).
  const activeEvidence = useMemo(() => occurrenceEvidence || evidenceList || [], [occurrenceEvidence, evidenceList]);

  const filteredIndices = useMemo(() => {
    if (!showPresentOnly) return activeEvidence.map((_, i) => i);
    return activeEvidence.reduce((acc, item, i) => {
      if ((item.presence || '').toLowerCase() !== 'absent') acc.push(i);
      return acc;
    }, []);
  }, [activeEvidence, showPresentOnly]);

  /** Pad/align a classifications array to evidenceLen, defaulting absent
   * samples to 'skipped' and present ones to '' -- shared default-shape
   * logic both modals already implemented identically. */
  const alignClassifications = useCallback((cls, evidence) => {
    if (!evidence || evidence.length === 0) return cls || [];
    const padded = evidence.map((e) => {
      const isAbsent = (e.presence || '').toLowerCase() === 'absent';
      return isAbsent ? { verdict: 'skipped', fpReason: '', scope_type: 'retailer_specific' } : { verdict: '', fpReason: '', scope_type: 'retailer_specific' };
    });
    (cls || []).forEach((c, i) => { if (c && i < padded.length) padded[i] = c; });
    return padded;
  }, []);

  const selectOccurrence = useCallback(async (occId) => {
    if (onPersistClassifications && localClassificationsRef.current) {
      const changed = localClassificationsRef.current.some(
        (c, i) => (c?.verdict || '') !== (initialClassificationsRef.current[i]?.verdict || '')
          || (c?.fpReason || '') !== (initialClassificationsRef.current[i]?.fpReason || '')
          || (c?.scope_type || '') !== (initialClassificationsRef.current[i]?.scope_type || '')
      );
      if (changed) {
        try { await onPersistClassifications(localClassificationsRef.current, selectedOccurrenceId); } catch { /* continue switching even if save fails */ }
      }
    }

    if (!occId || !anomalyId) {
      setSelectedOccurrenceId(null);
      setOccurrenceEvidence(null);
      setOccurrenceClassifications(null);
      setCurrentIndex(0);
      return;
    }

    setSelectedOccurrenceId(occId);
    setOccurrenceFetchInFlight(true);
    try {
      const occ = await api.getOccurrenceEvidence(anomalyId, occId);
      if (occ && occ.evidence_with_reasoning) {
        let parsed = null;
        try { parsed = JSON.parse(occ.evidence_with_reasoning); } catch { parsed = null; }
        setOccurrenceEvidence(parsed);
        // A manual occurrence switch (not a deep link) has no target index to
        // preserve -- jump to the first present sample (or 0) the same way
        // the original per-modal implementations did. A pending deep-link
        // target, if any, overrides this once it settles (see the merged
        // effect below), so this doesn't affect the deep-link path.
        if (parsed && !deepLinkTargetRef.current) {
          if (showPresentOnly) {
            const firstPresent = parsed.findIndex((e) => (e.presence || '').toLowerCase() !== 'absent');
            setCurrentIndex(firstPresent >= 0 ? firstPresent : 0);
          } else {
            setCurrentIndex(0);
          }
        }
      } else {
        setOccurrenceEvidence(null);
      }
      if (occ && occ.sample_classifications) {
        try { setOccurrenceClassifications(JSON.parse(occ.sample_classifications)); }
        catch { setOccurrenceClassifications(null); }
      } else {
        setOccurrenceClassifications(null);
      }
    } catch {
      setOccurrenceEvidence(null);
      setOccurrenceClassifications(null);
    } finally {
      setOccurrenceFetchInFlight(false);
    }
  }, [anomalyId, onPersistClassifications, selectedOccurrenceId, showPresentOnly]);

  // Occurrence list, for whichever UI (dropdown/date-picker) the consumer
  // renders. Guarded so this fetch resolving doesn't clobber a deep-linked
  // occurrence selection that's in flight or already applied -- unrelated to
  // this fetch, but previously always reset selectedOccurrenceId to null
  // regardless of why the modal opened.
  useEffect(() => {
    if (isOpen && anomalyId) {
      api.getAnomalyOccurrences(anomalyId).then((data) => {
        setOccurrences(data || []);
        if (!deepLinkTargetRef.current && !selectedOccurrenceIdRef.current) {
          setSelectedOccurrenceId(null);
          setOccurrenceEvidence(null);
          setOccurrenceClassifications(null);
        }
      }).catch(() => setOccurrences([]));
    } else {
      // Closing (or no anomalyId) always clears occurrence context -- unlike
      // the async .then() above, there's no in-flight race to protect here.
      setOccurrences([]);
      setSelectedOccurrenceId(null);
      setOccurrenceEvidence(null);
      setOccurrenceClassifications(null);
    }
  }, [isOpen, anomalyId]);

  // The single merged effect: seed-on-open (normal or deep-linked), then keep
  // currentIndex valid as filteredIndices/selectedOccurrenceId change. See
  // module docstring for why this must be one effect, not several.
  useEffect(() => {
    if (!isOpen) {
      openInitAppliedRef.current = false;
      deepLinkTargetRef.current = null;
      setDeepLinkPending(false);
      // A fetch that was in flight when the modal closed will resolve into
      // state nobody reads anymore -- without this, isLoading (which ORs in
      // occurrenceFetchInFlight) stays stuck true past close.
      setOccurrenceFetchInFlight(false);
      return;
    }
    if (!openInitAppliedRef.current) {
      openInitAppliedRef.current = true;
      setShowPresentOnly(initialShowPresentOnly);
      if (deepLinkTarget && deepLinkTarget.occurrenceId) {
        deepLinkTargetRef.current = deepLinkTarget;
        setDeepLinkPending(true);
        selectOccurrence(deepLinkTarget.occurrenceId);
      } else if (deepLinkTarget && Number.isInteger(deepLinkTarget.evidenceIndex)) {
        // Validate against filteredIndices now, not just via the "snap if
        // invalid" check below -- that check only re-runs when filteredIndices
        // itself changes (a real dependency-array entry), which never happens
        // for an index-only link against an already-loaded flat evidenceList
        // (no occurrence fetch, no showPresentOnly change to react to). An
        // out-of-range index would otherwise stick forever, rendering nothing.
        const idx = deepLinkTarget.evidenceIndex;
        setCurrentIndex(filteredIndices.includes(idx) ? idx : (filteredIndices[0] ?? 0));
      } else {
        setCurrentIndex(0);
      }
      return;
    }
    const pending = deepLinkTargetRef.current;
    if (pending && pending.occurrenceId === selectedOccurrenceId && !occurrenceFetchInFlight) {
      deepLinkTargetRef.current = null;
      setDeepLinkPending(false);
      if (Number.isInteger(pending.evidenceIndex) && filteredIndices.includes(pending.evidenceIndex)) {
        setCurrentIndex(pending.evidenceIndex);
        return;
      }
    }
    if (filteredIndices.length > 0 && !filteredIndices.includes(currentIndex)) {
      setCurrentIndex(filteredIndices[0]);
    }
  }, [isOpen, filteredIndices, selectedOccurrenceId, occurrenceFetchInFlight]); // eslint-disable-line react-hooks/exhaustive-deps

  // Classification source: the selected occurrence's own (once loaded), else
  // the anomaly-level fallback. One derivation, not two dueling effects each
  // independently deciding to overwrite localClassifications.
  useEffect(() => {
    let aligned;
    if (selectedOccurrenceId && occurrenceEvidence) {
      aligned = alignClassifications(occurrenceClassifications, occurrenceEvidence);
    } else if (!selectedOccurrenceId) {
      const canUseCanonical = !requireLatestOccurrenceClassifications || latestOccurrenceHasOwnClassifications;
      aligned = alignClassifications(canUseCanonical ? sampleClassifications : null, activeEvidence);
    } else {
      // selectedOccurrenceId set but its evidence hasn't loaded yet -- keep
      // whatever's showing (avoids a blank flash mid-fetch); the branch above
      // fires again once occurrenceEvidence arrives.
      return;
    }
    // Guard against re-firing on an equivalent result: upstream references
    // (evidenceList from a parent render, occurrenceEvidence from a fresh
    // JSON.parse) are not guaranteed stable, so without this check an
    // equivalent-but-new array would re-trigger this effect's own setState
    // every render -- an infinite loop, since setState changing
    // localClassifications is itself this effect isn't gated on, but a
    // re-render from ANY cause reruns alignClassifications with a fresh
    // array reference even when nothing meaningful changed.
    const prev = localClassificationsRef.current;
    const isSame = prev.length === aligned.length && prev.every((c, i) => (
      (c?.verdict || '') === (aligned[i]?.verdict || '')
      && (c?.fpReason || '') === (aligned[i]?.fpReason || '')
      && (c?.scope_type || '') === (aligned[i]?.scope_type || '')
    ));
    if (isSame) return;

    setLocalClassifications(aligned);
    localClassificationsRef.current = aligned;
    initialClassificationsRef.current = aligned;
  }, [selectedOccurrenceId, occurrenceEvidence, occurrenceClassifications, sampleClassifications, activeEvidence, alignClassifications, latestOccurrenceHasOwnClassifications, requireLatestOccurrenceClassifications]);

  const setVerdict = useCallback((index, verdict) => {
    setLocalClassifications((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], verdict, fpReason: verdict === 'false_positive' ? (next[index]?.fpReason || '') : '' };
      return next;
    });
  }, []);

  const setFpReason = useCallback((index, reason) => {
    setLocalClassifications((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], fpReason: reason };
      return next;
    });
  }, []);

  const setScopeType = useCallback((index, scopeType) => {
    setLocalClassifications((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], scope_type: scopeType };
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setOccurrenceEvidence(null);
    setOccurrenceClassifications(null);
    setSelectedOccurrenceId(null);
    setOccurrences([]);
    deepLinkTargetRef.current = null;
  }, []);

  return {
    currentIndex,
    setCurrentIndex,
    activeEvidence,
    filteredIndices,
    showPresentOnly,
    setShowPresentOnly,
    occurrences,
    selectedOccurrenceId,
    selectOccurrence,
    localClassifications,
    localClassificationsRef,
    initialClassificationsRef,
    setVerdict,
    setFpReason,
    setScopeType,
    isLoading: occurrenceFetchInFlight || deepLinkPending,
    reset,
  };
}

export default useEvidenceViewerState;
